import {
  type ApiResponsePromise,
  type ApiProgressEvent,
  makeRequest,
  type ApiResponseArgs,
  type PinType,
  toCorrectFormDataValue,
} from '@/js/libs/api';
import * as Types from './types';
import {
  type DateTimeType,
  type DateType,
  type DateTimeUnixType,
  encodeBody,
  fromDateType as fromDate,
  toDateType as toDate,
  fromDateTimeType as fromDateTime,
  toDateTimeUnixType as toDateTime,
} from '@/js/libs/api-helpers';

export { type DateTimeType, type DateTimeUnixType, fromDate, toDate, fromDateTime, toDateTime };

export type IdTranslationDto = { _id: 'ws-translate:TranslationDto'; toString: () => string };
export type getTranslationsParameters = {
  keyFilter?: string;
  keyValueFilter?: string;
  language?: string;
  /** page limit */ limit?: number;
  missing?: boolean;
  namespace?: string;
  order?: string;
  /** page number */ page?: number;
  /** sort order */ sort?: string;
};
export type getTranslationsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.PaginatedTranslations;
    headers: null;
  }>,
  Types.PaginatedTranslations
>;

export type getTranslationsSeparateParams = {
  parameters: getTranslationsParameters;
};
export type getTranslationsParamsUnion = getTranslationsParameters;
export type getTranslations = {
  (arg: {
    params: PinType<getTranslationsParamsUnion, getTranslationsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getTranslationsParamsUnion> | false;
  }): getTranslationsResponse;
};

/** Returns available translations for given language and namespace */ export const getTranslations: getTranslations =
  async ({
    params,
    onUploadProgress,
    signal,
    baseURL,
    isInternal,
    headers: headersParam = {},
    queryDefaults,
    ...otherParams
  }) => {
    const path = '/api/ws-translate/translations';
    const pathInternal = '/api/translations';
    let body: any = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
      const value = queryDefaults === false ? params.page : (params.page ?? queryDefaults?.['page']);
      if (value !== undefined) {
        urlParams.append('page', value.toString());
      }
    }
    {
      const value = queryDefaults === false ? params.limit : (params.limit ?? queryDefaults?.['limit']);
      if (value !== undefined) {
        urlParams.append('limit', value.toString());
      }
    }
    {
      const value = queryDefaults === false ? params.sort : (params.sort ?? queryDefaults?.['sort']);
      if (value !== undefined) {
        urlParams.append('sort', value.toString());
      }
    }
    {
      const value = queryDefaults === false ? params.order : (params.order ?? queryDefaults?.['order']);
      if (value !== undefined) {
        urlParams.append('order', value.toString());
      }
    }
    {
      const value = queryDefaults === false ? params.keyFilter : (params.keyFilter ?? queryDefaults?.['keyFilter']);
      if (value !== undefined) {
        urlParams.append('keyFilter', value.toString());
      }
    }
    {
      const value =
        queryDefaults === false ? params.keyValueFilter : (params.keyValueFilter ?? queryDefaults?.['keyValueFilter']);
      if (value !== undefined) {
        urlParams.append('keyValueFilter', value.toString());
      }
    }
    {
      const value = queryDefaults === false ? params.namespace : (params.namespace ?? queryDefaults?.['namespace']);
      if (value !== undefined) {
        urlParams.append('namespace', value.toString());
      }
    }
    {
      const value = queryDefaults === false ? params.missing : (params.missing ?? queryDefaults?.['missing']);
      if (value !== undefined) {
        urlParams.append('missing', value.toString());
      }
    }
    {
      const value = queryDefaults === false ? params.language : (params.language ?? queryDefaults?.['language']);
      if (value !== undefined) {
        urlParams.append('language', value.toString());
      }
    }

    const headers: Record<string, string> = {};
    headers['Accept'] = 'application/json';

    return makeRequest<ApiResponseArgs<getTranslationsResponse>>({
      ...otherParams,
      method: 'GET',
      url: isInternal ? pathInternal : path,
      params: urlParams,
      data: {},
      onUploadProgress,
      signal,
      baseURL: baseURL || undefined,
      headers: { ...headers, ...headersParam },
      responseTypes: 'json',
      requestType: 'json',
    });
  };
(getTranslations as any).paramsOrder = [{ type: 'parameters' }];

export type deleteTranslationsParameters = { ids: IdTranslationDto[] };
export type deleteTranslationsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 202;
    data: void;
    headers: null;
  }>,
  void
>;

export type deleteTranslationsSeparateParams = {
  parameters: deleteTranslationsParameters;
};
export type deleteTranslationsParamsUnion = deleteTranslationsParameters;
export type deleteTranslations = {
  (arg: {
    params: PinType<deleteTranslationsParamsUnion, deleteTranslationsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<deleteTranslationsParamsUnion> | false;
  }): deleteTranslationsResponse;
};

/** Deletes translations by IDs */ export const deleteTranslations: deleteTranslations = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.ids === undefined) {
    throw new Error('Missing required parameter: ids');
  }

  const path = '/api/ws-translate/translations';
  const pathInternal = '/api/translations';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.ids !== undefined) {
    params.ids.forEach((item) => urlParams.append('ids', item.toString()));
  }

  const headers: Record<string, string> = {};

  return makeRequest<ApiResponseArgs<deleteTranslationsResponse>>({
    ...otherParams,
    method: 'DELETE',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'void',
    requestType: 'json',
  });
};
(deleteTranslations as any).paramsOrder = [{ type: 'parameters' }];

export type getTranslationsByKeysParameters = { language?: string; namespace?: string };
export type getTranslationsByKeysResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Record<string, string>;
    headers: null;
  }>,
  Record<string, string>
>;

export type getTranslationsByKeysSeparateParams = {
  body: string[];
  parameters: getTranslationsByKeysParameters;
};
export type getTranslationsByKeysParamsUnion = { keys: string[] } & getTranslationsByKeysParameters;
export type getTranslationsByKeys = {
  (arg: {
    params: PinType<getTranslationsByKeysParamsUnion, getTranslationsByKeysSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getTranslationsByKeysParamsUnion> | false;
  }): getTranslationsByKeysResponse;
};

/** Returns available translations for selected keys by given language and namespace */ export const getTranslationsByKeys: getTranslationsByKeys =
  async ({
    params,
    onUploadProgress,
    signal,
    baseURL,
    isInternal,
    headers: headersParam = {},
    queryDefaults,
    ...otherParams
  }) => {
    if (params.keys === undefined) {
      throw new Error('Missing required body: keys');
    }

    const path = '/api/ws-translate/translations/by-keys';
    const pathInternal = '/api/translations/by-keys';
    let body: any = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.keys !== undefined) {
      body = params.keys;
    }
    {
      const value = queryDefaults === false ? params.language : (params.language ?? queryDefaults?.['language']);
      if (value !== undefined) {
        urlParams.append('language', value.toString());
      }
    }
    {
      const value = queryDefaults === false ? params.namespace : (params.namespace ?? queryDefaults?.['namespace']);
      if (value !== undefined) {
        urlParams.append('namespace', value.toString());
      }
    }

    const headers: Record<string, string> = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';

    return makeRequest<ApiResponseArgs<getTranslationsByKeysResponse>>({
      ...otherParams,
      method: 'POST',
      url: isInternal ? pathInternal : path,
      params: urlParams,
      data: encodeBody(body),
      onUploadProgress,
      signal,
      baseURL: baseURL || undefined,
      headers: { ...headers, ...headersParam },
      responseTypes: 'json',
      requestType: 'json',
    });
  };
(getTranslationsByKeys as any).paramsOrder = [{ type: 'body', paramName: 'keys' }, { type: 'parameters' }];

export type getAggregatedTranslationsParameters = { language?: string; namespace?: string };
export type getAggregatedTranslationsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.AggregatedTranslations[];
    headers: null;
  }>,
  Types.AggregatedTranslations[]
>;

export type getAggregatedTranslationsSeparateParams = {
  parameters: getAggregatedTranslationsParameters;
};
export type getAggregatedTranslationsParamsUnion = getAggregatedTranslationsParameters;
export type getAggregatedTranslations = {
  (arg: {
    params: PinType<getAggregatedTranslationsParamsUnion, getAggregatedTranslationsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getAggregatedTranslationsParamsUnion> | false;
  }): getAggregatedTranslationsResponse;
};

/** Returns available translations for given language and namespace */ export const getAggregatedTranslations: getAggregatedTranslations =
  async ({
    params,
    onUploadProgress,
    signal,
    baseURL,
    isInternal,
    headers: headersParam = {},
    queryDefaults,
    ...otherParams
  }) => {
    const path = '/api/ws-translate/translations-aggregated';
    const pathInternal = '/api/translations-aggregated';
    let body: any = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
      const value = queryDefaults === false ? params.language : (params.language ?? queryDefaults?.['language']);
      if (value !== undefined) {
        urlParams.append('language', value.toString());
      }
    }
    {
      const value = queryDefaults === false ? params.namespace : (params.namespace ?? queryDefaults?.['namespace']);
      if (value !== undefined) {
        urlParams.append('namespace', value.toString());
      }
    }

    const headers: Record<string, string> = {};
    headers['Accept'] = 'application/json';

    return makeRequest<ApiResponseArgs<getAggregatedTranslationsResponse>>({
      ...otherParams,
      method: 'GET',
      url: isInternal ? pathInternal : path,
      params: urlParams,
      data: {},
      onUploadProgress,
      signal,
      baseURL: baseURL || undefined,
      headers: { ...headers, ...headersParam },
      responseTypes: 'json',
      requestType: 'json',
    });
  };
(getAggregatedTranslations as any).paramsOrder = [{ type: 'parameters' }];

export type getGeneratedTranslationsParameters = { id: IdTranslationDto };
export type getGeneratedTranslationsResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.GeneratedTranslationDto;
    headers: null;
  }>,
  Types.GeneratedTranslationDto
>;

export type getGeneratedTranslationsSeparateParams = {
  parameters: getGeneratedTranslationsParameters;
};
export type getGeneratedTranslationsParamsUnion = getGeneratedTranslationsParameters;
export type getGeneratedTranslations = {
  (arg: {
    params: PinType<getGeneratedTranslationsParamsUnion, getGeneratedTranslationsSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getGeneratedTranslationsParamsUnion> | false;
  }): getGeneratedTranslationsResponse;
};

/** Generate missing translations with AI */ export const getGeneratedTranslations: getGeneratedTranslations = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.id === undefined) {
    throw new Error('Missing required parameter: id');
  }

  const path = '/api/ws-translate/translation/{id}/generate'.replaceAll('{id}', () =>
    encodeURIComponent(String(params.id)),
  );
  const pathInternal = '/api/translation/{id}/generate'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getGeneratedTranslationsResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getGeneratedTranslations as any).paramsOrder = [{ type: 'parameters' }];

export type updateTranslationParameters = { id: IdTranslationDto };
export type updateTranslationResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.TranslationDto;
    headers: null;
  }>,
  Types.TranslationDto
>;

export type updateTranslationSeparateParams = {
  body: Types.TranslationForm;
  parameters: updateTranslationParameters;
};
export type updateTranslationParamsUnion = { form: Types.TranslationForm } & updateTranslationParameters;
export type updateTranslation = {
  (arg: {
    params: PinType<updateTranslationParamsUnion, updateTranslationSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<updateTranslationParamsUnion> | false;
  }): updateTranslationResponse;
};

/** Update translation by id */ export const updateTranslation: updateTranslation = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.id === undefined) {
    throw new Error('Missing required parameter: id');
  }
  if (params.form === undefined) {
    throw new Error('Missing required body: form');
  }

  const path = '/api/ws-translate/translation/{id}'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  const pathInternal = '/api/translation/{id}'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.form !== undefined) {
    body = params.form;
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<updateTranslationResponse>>({
    ...otherParams,
    method: 'PUT',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(updateTranslation as any).paramsOrder = [{ type: 'body', paramName: 'form' }, { type: 'parameters' }];

export type deleteTranslationParameters = { id: IdTranslationDto };
export type deleteTranslationResponse = PinType<
  ApiResponsePromise<{
    responseCode: 202;
    data: void;
    headers: null;
  }>,
  void
>;

export type deleteTranslationSeparateParams = {
  parameters: deleteTranslationParameters;
};
export type deleteTranslationParamsUnion = deleteTranslationParameters;
export type deleteTranslation = {
  (arg: {
    params: PinType<deleteTranslationParamsUnion, deleteTranslationSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<deleteTranslationParamsUnion> | false;
  }): deleteTranslationResponse;
};

/** Delete translation by id */ export const deleteTranslation: deleteTranslation = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.id === undefined) {
    throw new Error('Missing required parameter: id');
  }

  const path = '/api/ws-translate/translation/{id}'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  const pathInternal = '/api/translation/{id}'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};

  return makeRequest<ApiResponseArgs<deleteTranslationResponse>>({
    ...otherParams,
    method: 'DELETE',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'void',
    requestType: 'json',
  });
};
(deleteTranslation as any).paramsOrder = [{ type: 'parameters' }];

export type cloneTranslationParameters = { id: IdTranslationDto };
export type cloneTranslationResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.TranslationDto;
    headers: null;
  }>,
  Types.TranslationDto
>;

export type cloneTranslationSeparateParams = {
  parameters: cloneTranslationParameters;
};
export type cloneTranslationParamsUnion = cloneTranslationParameters;
export type cloneTranslation = {
  (arg: {
    params: PinType<cloneTranslationParamsUnion, cloneTranslationSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<cloneTranslationParamsUnion> | false;
  }): cloneTranslationResponse;
};

/** Clone translation by id */ export const cloneTranslation: cloneTranslation = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.id === undefined) {
    throw new Error('Missing required parameter: id');
  }

  const path = '/api/ws-translate/translation/{id}/clone'.replaceAll('{id}', () =>
    encodeURIComponent(String(params.id)),
  );
  const pathInternal = '/api/translation/{id}/clone'.replaceAll('{id}', () => encodeURIComponent(String(params.id)));
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<cloneTranslationResponse>>({
    ...otherParams,
    method: 'POST',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(cloneTranslation as any).paramsOrder = [{ type: 'parameters' }];

export type addTranslationResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.TranslationDto;
    headers: null;
  }>,
  Types.TranslationDto
>;

export type addTranslationSeparateParams = {
  body: Types.TranslationForm;
};
export type addTranslationParamsUnion = { translation: Types.TranslationForm };
export type addTranslation = {
  (arg: {
    params: PinType<addTranslationParamsUnion, addTranslationSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<addTranslationParamsUnion> | false;
  }): addTranslationResponse;
};

export const addTranslation: addTranslation = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.translation === undefined) {
    throw new Error('Missing required body: translation');
  }

  const path = '/api/ws-translate/translation';
  const pathInternal = '/api/translation';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.translation !== undefined) {
    body = params.translation;
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<addTranslationResponse>>({
    ...otherParams,
    method: 'POST',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(addTranslation as any).paramsOrder = [{ type: 'body', paramName: 'translation' }];

export type addMissingTranslationResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: Types.TranslationDto;
    headers: null;
  }>,
  Types.TranslationDto
>;

export type addMissingTranslationSeparateParams = {
  body: Types.TranslationForm;
};
export type addMissingTranslationParamsUnion = { translation: Types.TranslationForm };
export type addMissingTranslation = {
  (arg: {
    params: PinType<addMissingTranslationParamsUnion, addMissingTranslationSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<addMissingTranslationParamsUnion> | false;
  }): addMissingTranslationResponse;
};

export const addMissingTranslation: addMissingTranslation = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  if (params.translation === undefined) {
    throw new Error('Missing required body: translation');
  }

  const path = '/api/ws-translate/translation/add-missing';
  const pathInternal = '/api/translation/add-missing';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  if (params.translation !== undefined) {
    body = params.translation;
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';
  headers['Content-Type'] = 'application/json';

  return makeRequest<ApiResponseArgs<addMissingTranslationResponse>>({
    ...otherParams,
    method: 'POST',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: encodeBody(body),
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(addMissingTranslation as any).paramsOrder = [{ type: 'body', paramName: 'translation' }];

export type getNamespacesParameters = { filter?: string };
export type getNamespacesResponse = PinType<
  ApiResponsePromise<{
    responseCode: 200;
    data: string[];
    headers: null;
  }>,
  string[]
>;

export type getNamespacesSeparateParams = {
  parameters: getNamespacesParameters;
};
export type getNamespacesParamsUnion = getNamespacesParameters;
export type getNamespaces = {
  (arg: {
    params: PinType<getNamespacesParamsUnion, getNamespacesSeparateParams>;
    onUploadProgress?: (event: ApiProgressEvent) => void;
    signal?: AbortSignal;
    baseURL?: string;
    isInternal?: boolean;
    headers?: Record<string, string>;
    queryDefaults?: Partial<getNamespacesParamsUnion> | false;
  }): getNamespacesResponse;
};

/** Returns all namespace */ export const getNamespaces: getNamespaces = async ({
  params,
  onUploadProgress,
  signal,
  baseURL,
  isInternal,
  headers: headersParam = {},
  queryDefaults,
  ...otherParams
}) => {
  const path = '/api/ws-translate/translations/namespaces';
  const pathInternal = '/api/translations/namespaces';
  let body: any = {};
  const formData = new FormData();
  const urlParams = new URLSearchParams();
  {
    const value = queryDefaults === false ? params.filter : (params.filter ?? queryDefaults?.['filter']);
    if (value !== undefined) {
      urlParams.append('filter', value.toString());
    }
  }

  const headers: Record<string, string> = {};
  headers['Accept'] = 'application/json';

  return makeRequest<ApiResponseArgs<getNamespacesResponse>>({
    ...otherParams,
    method: 'GET',
    url: isInternal ? pathInternal : path,
    params: urlParams,
    data: {},
    onUploadProgress,
    signal,
    baseURL: baseURL || undefined,
    headers: { ...headers, ...headersParam },
    responseTypes: 'json',
    requestType: 'json',
  });
};
(getNamespaces as any).paramsOrder = [{ type: 'parameters' }];

export * from './types';
export * from './constants';
